import { IndexSort } from "@omni/types/api/search/algolia/shared/enum/indexSort.enum";

import { NumericFacetBounds } from "@/components/Search/SearchResults/components/results/components/productsResults/components/facets/components/facet/@types/numericFacet.types";
import {
    SearchComponent,
    SearchComponentEnum,
    SearchComponentOfType
} from "@/utils/search/types/searchComponent";

export const FacetOperator = {
    AND: "AND",
    OR: "OR"
} as const;
export type FacetOperatorEnum = keyof typeof FacetOperator;

export type FacetFilter = {
    name: string;
    operator: FacetOperatorEnum;
    selectedValues: Array<string>;
};

export type NumericFilter = {
    name: string;
} & NumericFacetBounds;

type _ResultListConfiguration<
    TComponent extends SearchComponentOfType<"RESULT_LIST">
> = {
    component: TComponent;
};
type _FacetsConfiguration<TComponent extends SearchComponentOfType<"FACETS">> =
    {
        component: TComponent;
        facetFilters: Array<FacetFilter>;
        numericFilters: Array<NumericFilter>;
    };
type _HitsPerPageConfiguration<
    TComponent extends SearchComponentOfType<"HITS_PER_PAGE">
> = {
    component: TComponent;
    hitsPerPage: number;
};
type _PaginationConfiguration<
    TComponent extends SearchComponentOfType<"PAGINATION">
> = {
    component: TComponent;
    hitsWithInjected: number;
    hitsWithoutInjected: number;
    hitsRequested?: number;
    currentPage?: number;
    page?: number;
};
type _SortConfiguration<TComponent extends SearchComponentOfType<"SORT">> = {
    component: TComponent;
    sortBy: IndexSort;
};
type _NumberOfHitsConfiguration<
    TComponent extends SearchComponentOfType<"NUMBER_OF_HITS">
> = {
    component: TComponent;
};

export type SearchComponentConfigurationMap = {
    [SearchComponent.PRODUCT_RESULT_LIST]: _ResultListConfiguration<"PRODUCT_RESULT_LIST">;
    [SearchComponent.PRODUCT_HITS_PER_PAGE]: _HitsPerPageConfiguration<"PRODUCT_HITS_PER_PAGE">;
    [SearchComponent.PRODUCT_PAGINATION]: _PaginationConfiguration<"PRODUCT_PAGINATION">;
    [SearchComponent.PRODUCT_NUMBER_OF_HITS]: _NumberOfHitsConfiguration<"PRODUCT_NUMBER_OF_HITS">;
    [SearchComponent.PRODUCT_FACETS]: _FacetsConfiguration<"PRODUCT_FACETS">;
    [SearchComponent.PRODUCT_SORT]: _SortConfiguration<"PRODUCT_SORT">;
    [SearchComponent.CONTENT_RESULT_LIST]: _ResultListConfiguration<"CONTENT_RESULT_LIST">;
    [SearchComponent.CONTENT_HITS_PER_PAGE]: _HitsPerPageConfiguration<"CONTENT_HITS_PER_PAGE">;
    [SearchComponent.CONTENT_PAGINATION]: _PaginationConfiguration<"CONTENT_PAGINATION">;
    [SearchComponent.CONTENT_NUMBER_OF_HITS]: _NumberOfHitsConfiguration<"CONTENT_NUMBER_OF_HITS">;
};

export type SearchComponentConfiguration =
    SearchComponentConfigurationMap[keyof SearchComponentConfigurationMap];

export type ResultListConfiguration = _ResultListConfiguration<
    SearchComponentOfType<"RESULT_LIST">
>;
export type FacetsConfiguration = _FacetsConfiguration<
    SearchComponentOfType<"FACETS">
>;
export type HitsPerPageConfiguration = _HitsPerPageConfiguration<
    SearchComponentOfType<"HITS_PER_PAGE">
>;
export type PaginationConfiguration = _PaginationConfiguration<
    SearchComponentOfType<"PAGINATION">
>;
export type SortConfiguration = _SortConfiguration<
    SearchComponentOfType<"SORT">
>;
export type NumberOfHitsConfiguration = _NumberOfHitsConfiguration<
    SearchComponentOfType<"NUMBER_OF_HITS">
>;

type Props<TComponent extends { component: SearchComponentEnum }> = Omit<
    TComponent,
    "component"
>;

export type AnySearchConfiguration = Partial<
    Props<ResultListConfiguration> &
        Props<FacetsConfiguration> &
        Props<HitsPerPageConfiguration> &
        Props<PaginationConfiguration> &
        Props<SortConfiguration> &
        Props<NumberOfHitsConfiguration>
>;
